import _ from "lodash";
import React, { Component } from "react";

import { api } from "@/APIRequests";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { hasAccessToPaymentTab } from "@/utils/authUtils";
import { isUserAdmin } from "@/redux/actions/auth";
import MemberProfileAdmin from "@/components/Provider/PatientView/MemberProfile/MemberProfileAdmin";
import MemberProfileCarePlan from "@/components/Provider/PatientView/MemberProfile/MemberProfileCarePlan";
import MemberProfileMedical from "@/components/Provider/PatientView/MemberProfile/MemberProfileMedical";
import { MemberProfileReports } from "@/components/Provider/PatientView/MemberProfile/Reports/MemberProfileReports";
import MemberProfileTimeline from "@/components/Provider/PatientView/MemberProfile/MemberProfileTimeline";
import { Payment } from "@/components/Provider/PatientView/MemberProfile/Payment/Payment";
import { ViewContent2 } from "@/components/layout/ViewContent";

import { EligibilityAlertBanner } from "./components/EligibilityAlertBanner";
import { MemberProfileHeader } from "./(header)/MemberProfileHeader";
import { MemberProfileNotes } from "./(notes)/MemberProfileNotes";
import { PAYMENT_TAB } from "./constants";
import { PaymentAlertBanner } from "./components/PaymentAlertBanner";
import { SchedulingRestrictedBanner } from "./components/SchedulingRestrictedBanner";
import "./MemberProfile.styles.scss";

export class MemberProfile extends Component {
  buildInitialState() {
    // URLSearchParams.get() correctly decodes "+" as space (form-encoding); decodeURIComponent does not
    const selectedTab =
      new URLSearchParams(this.props.location.search).get("selectedTab") || "Care Plan";

    return {
      carePlan: null,
      carePlanData: {
        carePlanFile: null,
        careTeam: null,
      },
      eligibilityDetails: this.props.patientDetails?.primary_eligibility_record || {},
      files: [],
      isCarePlanLoaded: false,
      medicalData: {
        insuranceImageUrls: {
          pbUrl: null,
          pfUrl: null,
          sbUrl: null,
          sfUrl: null,
        },
      },
      patientDetails: this.props.patientDetails,

      patientId: this.props.match.params.patientId,
      selectedTab: selectedTab,
      timelineData: {
        activities: [],
        practiceName: null,
      },
    };
  }

  constructor(props) {
    super(props);
    this.adminFocusRef = React.createRef();
    this.isAdmin = isUserAdmin();
    this.state = this.buildInitialState();
  }

  async componentDidMount() {
    const files = await api.shared.get_files({
      entity_id: this.state.patientId,
      entity_type: "patient",
    });

    this.setState({ files });

    await this.getCarePlan();

    if (this.state.selectedTab === "Medical" && this.isAdmin) {
      await this.getEligibilityDetails(this.props.patientDetails);
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.search !== this.props.location.search) {
      const selectedTab =
        new URLSearchParams(this.props.location.search).get("selectedTab") || "Care Plan";

      this.setState({ selectedTab });
    }

    // Sync local state when React Query delivers updated patientDetails via props
    if (prevProps.patientDetails !== this.props.patientDetails) {
      this.setState({
        eligibilityDetails: this.props.patientDetails?.primary_eligibility_record || {},
        patientDetails: this.props.patientDetails,
      });
    }

    // Explicitly reset all state on patientId change to minimize risk of leftover state as multiple API calls
    // are involved. An explicit return is used (rather than using the shared 'patientIdChange' function) to prevent
    // a race condition with the other tab-switching behavior
    if (prevProps.match.params.patientId !== this.props.match.params.patientId) {
      this.setState(this.buildInitialState(), () => {
        this.componentDidMount();
      });

      return;
    }

    if (prevProps.waitlistChangeCount !== this.props.waitlistChangeCount) {
      await this.getActivities();
    }

    if (prevState.selectedTab !== this.state.selectedTab && this.state.selectedTab === "Medical") {
      if (this.isAdmin) {
        await this.getEligibilityDetails();
      }
    }
  }

  async getCarePlan() {
    const options = { params: { showLoader: true } };
    const urlParams = { patientId: this.state.patientId };
    const response = await api.shared.fetch_patient_care_plan_v2({ options, urlParams });

    this.setState({
      carePlan: response,
      isCarePlanLoaded: true,
    });
  }

  async getEligibilityDetails(patientDetailsParam) {
    const patientDetails = patientDetailsParam || this.state.patientDetails;
    const eligibilityRecordId = patientDetails?.primary_eligibility_record?.id;

    if (!eligibilityRecordId) {
      return;
    }

    try {
      const data = {
        id: eligibilityRecordId,
        query_type: "id",
      };

      const eligibilityResponse = await api.admin.fetch_insurance_eligibility_records({ data });

      if (eligibilityResponse) {
        this.setState({ eligibilityDetails: eligibilityResponse });
      }
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Error fetching eligibility details" }), {
        tags: {
          action: "getEligibilityDetails",
          component: "MemberProfile",
          errorType: "query",
          patientId: this.state.patientId,
        },
      });
    }
  }

  async getActivities() {
    let urlParams = { patientId: this.state.patientId };
    const options = { params: { showLoader: true } };

    try {
      const response = await api.shared.fetch_patient_activity_feed({ options, urlParams });

      this.setState({
        timelineData: {
          ...this.state.timelineData,
          activities: response.results,
        },
      });
    } catch (error) {
      console.log(error);
    }
  }

  getAssociatedFiles(files) {
    return files.filter((file) => {
      if (file?.file_sub_type === "ASSOCIATED_FILE_PATIENT") {
        return file;
      }
    });
  }

  renderCarePlanLoading = () => {
    // Stop gap until sticky footers are added
    return <div style={{ height: "100px" }} />;
  };

  render() {
    const showRestrictedSchedulingBanner = this?.state?.patientDetails?.is_scheduling_restricted;

    const selectedTabChangeHandler = (tab) => {
      const searchParams = new URLSearchParams(this.props.history.location.search);

      searchParams.set("selectedTab", tab);

      const newTabPath = `${this.props.history.location.pathname}?${searchParams.toString()}`;

      this.props.history.push(newTabPath);
      this.setState({ selectedTab: tab });
    };

    const paywallStatus = this.state.patientDetails?.paywall_status;
    const patientName =
      this.state.patientDetails?.preferred_name || this.state.patientDetails.first_name;
    const automatic_payment_enabled = this.state.patientDetails?.automatic_payment_enabled;

    return (
      <ViewContent2 {...getAutomationDataAttr("MemberProfilePage")}>
        <PaymentAlertBanner
          automaticPaymentEnabled={automatic_payment_enabled}
          patientName={patientName}
          paywallStatus={paywallStatus}
          softPaywallTimestamp={this.state.patientDetails?.soft_paywall}
          {...getAutomationDataAttr("patient-profile-payment-alert-banner")}
        />

        <SchedulingRestrictedBanner
          isAdmin={this.isAdmin}
          isSchedulingRestricted={!paywallStatus?.paywall && showRestrictedSchedulingBanner}
          onNavigateToAdmin={() => {
            selectedTabChangeHandler("Admin");
            setTimeout(() => this?.adminFocusRef?.current?.scrollIntoView(), 0);
          }}
          {...getAutomationDataAttr("patient-profile-scheduling-restricted-banner")}
        />

        <EligibilityAlertBanner
          eligibilityStatus={this.state.eligibilityDetails?.status}
          firstName={this.state.patientDetails.first_name}
          {...getAutomationDataAttr("patient-profile-eligibility-alert-banner")}
        />

        <MemberProfileHeader />

        {this.state.selectedTab === "Reports" && <MemberProfileReports data={this.state.files} />}

        {this.state.selectedTab === "Care Plan" && this.state.isCarePlanLoaded ? (
          <MemberProfileCarePlan
            carePlan={this.state.carePlan}
            data={this.state.carePlanData}
            patientDetails={this.state.patientDetails}
            updateState={(newState) => this.setState(newState)}
          />
        ) : (
          this.state.selectedTab === "Care Plan" && this.renderCarePlanLoading()
        )}

        {this.state.selectedTab === "Notes" && <MemberProfileNotes />}

        {this.state.selectedTab === "Medical" && (
          <MemberProfileMedical
            data={this.state.medicalData}
            eligibilityDetails={this.state.eligibilityDetails}
            files={this.getAssociatedFiles(this.state.files)}
            patientDetails={this.state.patientDetails}
            updateState={(newState) => {
              const updatedState = {
                ...newState,
                files: [..._.get(newState, "files", []), ...this.state.files],
              };

              this.setState(updatedState);
            }}
          />
        )}

        {this.state.selectedTab === "Timeline" && (
          <MemberProfileTimeline
            data={this.state.timelineData}
            patientDetails={this.state.patientDetails}
            updateState={(newState) => this.setState(newState)}
          />
        )}

        {hasAccessToPaymentTab() && this.state.selectedTab === PAYMENT_TAB && (
          <Payment patientDetails={this.state.patientDetails} />
        )}

        {this.isAdmin && this.state.selectedTab === "Admin" && (
          <MemberProfileAdmin
            adminFocusRef={this.adminFocusRef}
            patientDetails={this.state.patientDetails}
            refetchDetails={async () => {
              await this.props.refetchPatientDetails();
              await this.getActivities();
            }}
            selectedTabChangeHandler={selectedTabChangeHandler}
            timelineData={this.state.timelineData}
            updateState={(newState) => this.setState(newState)}
          />
        )}
      </ViewContent2>
    );
  }
}
